import * as React from "react"
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
    SortingState,
    getSortedRowModel,
    getFilteredRowModel,
    ColumnFiltersState,
} from "@tanstack/react-table"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, Search } from "lucide-react"

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    isLoading?: boolean
    searchKey?: string
    searchPlaceholder?: string
    renderExpandedRow?: (row: TData) => React.ReactNode
    expandedRowId?: string
    emptyState?: React.ReactNode
}

export function DataTable<TData, TValue>({
    columns,
    data,
    isLoading,
    searchKey,
    searchPlaceholder = "Filtrar...",
    renderExpandedRow,
    expandedRowId,
    emptyState
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    })

    return (
        <div className="space-y-4">
            {/* Filters (Optional) */}
            {searchKey && (
                <div className="flex items-center py-4">
                    <div className="relative max-w-sm w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            placeholder={searchPlaceholder}
                            value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ""}
                            onChange={(event) =>
                                table.getColumn(searchKey)?.setFilterValue(event.target.value)
                            }
                            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 pl-10 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-800 dark:bg-gray-950 dark:ring-offset-gray-950 dark:placeholder:text-gray-400 dark:focus-visible:ring-gray-300 transition-all shadow-sm"
                        />
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                key={header.id}
                                                className="h-12 px-6 align-middle font-medium text-gray-500 dark:text-gray-400 [&:has([role=checkbox])]:pr-0 whitespace-nowrap"
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="[&_tr:last-child]:border-0">
                            {isLoading ? (
                                <tr className="border-b border-gray-100 dark:border-gray-800">
                                    <td colSpan={columns.length} className="h-24 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Carregando...
                                        </div>
                                    </td>
                                </tr>
                            ) : table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => {
                                    const id = (row.original as any).uuid || (row.original as any).email || row.id;
                                    const isExpanded = expandedRowId === id;
                                    return (
                                        <React.Fragment key={row.id}>
                                            <tr
                                                className={`border-b border-gray-100 dark:border-gray-800 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-800/50 data-[state=selected]:bg-gray-100 ${
                                                    isExpanded ? "bg-blue-50/20 dark:bg-blue-900/5" : ""
                                                }`}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id} className="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                            {renderExpandedRow && isExpanded && (
                                                <tr className="bg-gray-50/40 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-800">
                                                    <td colSpan={columns.length} className="p-0">
                                                        <div className="overflow-hidden">
                                                            {renderExpandedRow(row.original)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="py-16 px-4 text-center">
                                        {emptyState ? (
                                            emptyState
                                        ) : (
                                            <div className="flex flex-col items-center justify-center space-y-3 max-w-sm mx-auto">
                                                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl text-gray-400 dark:text-gray-500 animate-pulse border border-gray-100 dark:border-gray-800/80">
                                                    <Search className="h-8 w-8" />
                                                </div>
                                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    Nenhum resultado encontrado
                                                </h3>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                                                    Não encontramos dados correspondentes para os filtros ou termos de pesquisa digitados no momento.
                                                </p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-2">
                <div className="flex-1 text-sm text-gray-600 dark:text-gray-400">
                    {table.getFilteredSelectedRowModel().rows.length > 0 && (
                        <span>
                            {table.getFilteredSelectedRowModel().rows.length} de{" "}
                            {table.getFilteredRowModel().rows.length} linha(s) selecionada(s).
                        </span>
                    )}
                    Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        className="h-8 w-8 p-0 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50 transition-colors dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </button>
                    <button
                        className="h-8 w-8 p-0 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50 transition-colors dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        className="h-8 w-8 p-0 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50 transition-colors dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                        className="h-8 w-8 p-0 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50 transition-colors dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    )
}
